import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import LandingPage from '@/pages/LandingPage';
import Dashboard from '@/pages/Dashboard';
import NewDocumentationPage from '@/pages/NewDocumentationPage';
import NewUseCasesPage from '@/pages/NewUseCasesPage';
import SourcesPage from '@/pages/SourcesPage';
import PublicPricingPage from '@/pages/PublicPricingPage';
import { ProvenanceIndexPage, ProvenanceCountryPage } from '@/pages/ProvenancePage';
import MobileApp from '@/pages/MobileApp';
import { Toaster } from '@/components/ui/sonner';
import { LanguageProvider } from '@/contexts/LanguageContext';
import AnalyticsConsentBanner from '@/components/AnalyticsConsentBanner';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import { useAnalytics } from '@/hooks/useAnalytics';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AppRoutes({ user, handleLogin, handleLogout }) {
  const location = useLocation();
  // Wire the analytics tracker once at the route-aware level.
  useAnalytics(user);
  // Use the top-level path segment so nested /platform/* transitions are handled by Dashboard.
  const topKey = '/' + (location.pathname.split('/')[1] || '');
  return (
    <div key={topKey} className="page-transition">
      <Routes>
        {/* Public pages */}
        <Route path="/" element={<LandingPage user={user} onLogout={handleLogout} />} />
        <Route path="/documentation" element={<NewDocumentationPage user={user} onLogout={handleLogout} />} />
        <Route path="/use-cases" element={<NewUseCasesPage user={user} onLogout={handleLogout} />} />
        <Route path="/sources" element={<SourcesPage user={user} onLogout={handleLogout} />} />
        <Route path="/pricing" element={<PublicPricingPage user={user} onLogout={handleLogout} />} />
        {/* Public Data Provenance — strategic moat, zero auth required */}
        <Route path="/provenance" element={<ProvenanceIndexPage user={user} />} />
        <Route path="/provenance/:slug" element={<ProvenanceCountryPage user={user} />} />
        {/* Auth */}
        <Route path="/login" element={user ? <Navigate to={new URLSearchParams(window.location.search).get('redirect') || '/platform'} /> : <LoginPage onLogin={handleLogin} />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Protected */}
        <Route path="/platform/*" element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
        {/* Mobile PWA (installable) */}
        <Route path="/app/*" element={user ? <MobileApp user={user} onLogout={handleLogout} /> : <Navigate to="/login?redirect=/app" />} />
      </Routes>
    </div>
  );
}

// Global axios interceptor — attach auth token to all requests
axios.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global axios response interceptor — catch 401s system-wide and bounce the
// user cleanly to /login instead of leaking raw {"detail":"Not authenticated"}
// blobs into the UI (e.g. on the PDF download path when the session expired).
// Skipped for /auth/* endpoints so a failed login doesn't recursively redirect.
axios.interceptors.response.use(
  (resp) => resp,
  (error) => {
    try {
      const url = error?.config?.url || '';
      const status = error?.response?.status;
      const onLoginPage = window.location.pathname.startsWith('/login');
      if (status === 401 && !onLoginPage && !/\/auth\//.test(url)) {
        localStorage.removeItem('token');
        // Only redirect if we're inside the authenticated app shell
        if (window.location.pathname.startsWith('/platform')) {
          setTimeout(() => {
            window.location.href = '/login?session_expired=1';
          }, 400);
        }
      }
    } catch { /* noop — never let the interceptor itself throw */ }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser(res.data))
        .catch(() => { localStorage.removeItem('token'); setUser(null); })
        .finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  const handleLogin = (token) => {
    localStorage.setItem('token', token);
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setUser(res.data));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('impersonator_email');
    setUser(null);
    window.location.href = '/';
  };

  if (checking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0f1a]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <LanguageProvider>
    <div className="App">
      <BrowserRouter>
        <ImpersonationBanner />
        <AppRoutes user={user} handleLogin={handleLogin} handleLogout={handleLogout} />
      </BrowserRouter>
      <Toaster position="top-right" />
      <AnalyticsConsentBanner />
    </div>
    </LanguageProvider>
  );
}

export default App;
